import { useResetPassword } from '@/hooks/useAuth';
import { ResetPasswordData } from '@/types/auth.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'auth.validation.password.minLength')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'auth.validation.password.strength'
      ),
    confirmPassword: z.string(),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'auth.validation.password.mismatch',
    path: ['confirmPassword'],
  });

export const ResetPasswordForm = () => {
  const { t } = useTranslation();
  const resetPassword = useResetPassword();
  const [searchParams] = useSearchParams();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordData) => {
    try {
      const token = searchParams.get('token');
      if (!token) {
        toast.error('Reset token is missing or invalid');
        return;
      }
      await resetPassword.mutateAsync({ ...data, token });
      setIsSubmitted(true);
    } catch {
      // Error is handled by the useResetPassword hook with toast
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center">
        <h3 className="text-lg font-medium text-neutral-900">
          {t('auth.resetPassword.success.title')}
        </h3>
        <p className="mt-2 text-sm text-neutral-600">
          {t('auth.resetPassword.success.message')}
        </p>
        <Link
          to="/login"
          className="text-primary-600 hover:text-primary-500 mt-4 inline-block text-sm font-medium"
        >
          {t('auth.resetPassword.success.backToLogin')}
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label
          htmlFor="newPassword"
          className="block text-sm font-medium text-neutral-700"
        >
          {t('auth.resetPassword.newPassword')}
        </label>
        <input
          {...register('newPassword')}
          type="password"
          id="newPassword"
          className="focus:border-primary-500 focus:ring-primary-500 mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:ring-1 focus:outline-none"
          placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
        />
        {errors.newPassword && (
          <p className="text-error-600 mt-1 text-sm">
            {t(errors.newPassword.message as string)}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="confirmPassword"
          className="block text-sm font-medium text-neutral-700"
        >
          {t('auth.resetPassword.confirmPassword')}
        </label>
        <input
          {...register('confirmPassword')}
          type="password"
          id="confirmPassword"
          className="focus:border-primary-500 focus:ring-primary-500 mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:ring-1 focus:outline-none"
          placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
        />
        {errors.confirmPassword && (
          <p className="text-error-600 mt-1 text-sm">
            {t(errors.confirmPassword.message as string)}
          </p>
        )}
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 flex w-full justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
        >
          {isSubmitting ? t('common.loading') : t('auth.resetPassword.submit')}
        </button>
      </div>

      <div className="text-center text-sm">
        <Link
          to="/login"
          className="text-primary-600 hover:text-primary-500 font-medium"
        >
          {t('auth.resetPassword.backToLogin')}
        </Link>
      </div>
    </form>
  );
};
